import { z } from 'zod';

/**
 * A value where every nested object field is optional. Arrays are left whole — a partial update
 * replaces a list, it does not merge into it.
 */
export type DeepPartial<T> = {
  [K in keyof T]?: T[K] extends readonly unknown[]
    ? T[K]
    : T[K] extends object
      ? DeepPartial<T[K]>
      : T[K];
};

/**
 * Unwraps the wrappers that supply a value when one is missing or invalid.
 *
 * `.nullable()` is deliberately kept: `null` is a value the contract allows, not a fallback.
 */
const unwrapFallbacks = (schema: z.ZodType): z.ZodType => {
  if (schema instanceof z.ZodCatch || schema instanceof z.ZodDefault) {
    return unwrapFallbacks(schema.def.innerType as z.ZodType);
  }

  if (schema instanceof z.ZodOptional) {
    return unwrapFallbacks(schema.def.innerType as z.ZodType);
  }

  return schema;
};

/**
 * Builds a write schema from a read schema by making every field, at every depth, optional.
 *
 * Zod 4 removed `.deepPartial()`, but settings payloads need it — clients save one nested field at
 * a time. Deriving the payload schema from the entity schema also keeps the two from drifting as
 * fields are added.
 *
 * `.catch(...)` and `.default(...)` are stripped on the way down: they exist so that *reading*
 * stored settings tolerates legacy or invalid data, whereas a *write* carrying an invalid value
 * must be rejected with a 400 rather than silently recorded as the default.
 */
export const deepPartial = <T extends z.ZodObject>(schema: T): z.ZodObject => {
  const shape: Record<string, z.ZodType> = {};

  for (const [key, field] of Object.entries(schema.shape)) {
    const unwrapped = unwrapFallbacks(field as z.ZodType);

    shape[key] = (unwrapped instanceof z.ZodObject ? deepPartial(unwrapped) : unwrapped).optional();
  }

  return z.object(shape);
};
